/**
 * Pixel runtime configuration. The shape is intentionally minimal so it
 * fits in a single inline `<script>` block on a Webflow page.
 */

export type FieldMapEntry =
  | string
  | { split: [string, string] }
  | { value: string; fixes?: Record<string, string> };

export interface PixelFormConfig {
  /** CSS selector for the form (e.g. '#wf-form-New-Landing-Page-Form'). */
  selector: string;
  /** Discriminator stored on marketing.form_submissions.form_name. */
  formId: string;
  /**
   * Meta Pixel event name. Must match the event name any server-side
   * sender (Cortana since PED-111) uses so eventID dedup works. Defaults
   * map: landing-page -> Lead, cal-booking -> Schedule, lp-*-step-2 ->
   * CompleteRegistration.
   */
  pixelEventName?: 'Lead' | 'CompleteRegistration' | 'Schedule';
  /** Optional GTM dataLayer event name fired alongside the POST. */
  dataLayerEventName?: string;
  /** Optional PostHog event name fired alongside the POST. */
  posthogEventName?: string;
  /** Webflow field name -> backend data key mapping. */
  fieldMap?: Record<string, FieldMapEntry>;
  /**
   * Per-form override of PixelConfig.apiPost. When resolved to false the
   * pixel skips its POST to /api/v1/forms/submit for this form only; all
   * other channels (fbq, dataLayer, PostHog, hidden-input injection) fire
   * unchanged. Unset falls through to the global flag.
   */
  apiPost?: boolean;
}

export interface PixelCalConfig {
  enabled?: boolean;
  /** Cal.com namespace (e.g. 'consultation'). */
  namespace?: string;
  /** Form discriminator persisted alongside the booking POST. */
  formId?: string;
}

export interface PixelUtmLinkDecorationConfig {
  enabled?: boolean;
  /**
   * Only decorate outbound links pointing at this host or any of its
   * subdomains. Match is exact-host OR `.targetHost` suffix; substring
   * matches are NOT accepted (so `get.example.com.evil.com` does not match
   * `get.example.com`).
   */
  targetHost?: string;
}

export interface PixelConfig {
  /** Backend base URL. POST target is `${apiBaseUrl}/api/v1/forms/submit`. */
  apiBaseUrl: string;
  /** One entry per form on the page. */
  forms: PixelFormConfig[];
  cal?: PixelCalConfig;
  utmLinkDecoration?: PixelUtmLinkDecorationConfig;
  /** Optional GA4 measurement ID (e.g. 'G-XXXXXXXX'). */
  ga4MeasurementId?: string;
  /**
   * Optional override for the browser-side Meta Lead/Schedule prod-host
   * allowlist. When unset, defaults to ignitionbenefits.com +
   * www.ignitionbenefits.com. Match is exact-host, case-insensitive, no
   * suffix matching. See `host.ts`.
   */
  prodHosts?: string[];
  /**
   * When false, the pixel does not POST submissions to
   * `${apiBaseUrl}/api/v1/forms/submit` (browser fbq/dataLayer/PostHog and
   * hidden attribution-input injection are unaffected). Set false once an
   * external transport (Zapier, PED-99) owns the server-side delivery for
   * Webflow-native forms. Defaults to true. Per-form override:
   * PixelFormConfig.apiPost.
   */
  apiPost?: boolean;
  /** Verbose logging via console.warn. Off by default. */
  debug?: boolean;
}

declare global {
  interface Window {
    IgnitionPixelConfig?: PixelConfig;
    IgnitionPixel?: { configure: (cfg: PixelConfig) => void; version: string };
  }
}

/**
 * Validate the config at runtime. Returns a normalized PixelConfig or
 * undefined when the input is unusable. Logs to console.warn so a
 * misconfigured pixel does not silently no-op.
 */
export function validateConfig(input: unknown): PixelConfig | undefined {
  if (!input || typeof input !== 'object') {
    warn('config missing or not an object');
    return undefined;
  }
  const raw = input as Record<string, unknown>;
  const apiBaseUrl = typeof raw.apiBaseUrl === 'string' ? raw.apiBaseUrl.trim() : '';
  if (!apiBaseUrl) {
    warn('config.apiBaseUrl is required');
    return undefined;
  }
  const formsRaw = Array.isArray(raw.forms) ? raw.forms : [];
  const forms: PixelFormConfig[] = [];
  for (const f of formsRaw) {
    const formCfg = validateForm(f);
    if (formCfg) forms.push(formCfg);
  }
  const cal = validateCal(raw.cal);
  const utmLinkDecoration = validateUtmLinkDecoration(raw.utmLinkDecoration);
  const ga4MeasurementId =
    typeof raw.ga4MeasurementId === 'string' && raw.ga4MeasurementId.trim()
      ? raw.ga4MeasurementId.trim()
      : undefined;
  const prodHosts = validateProdHosts(raw.prodHosts);
  const apiPost = raw.apiPost !== false;
  const debug = raw.debug === true;
  return { apiBaseUrl, forms, cal, utmLinkDecoration, ga4MeasurementId, prodHosts, apiPost, debug };
}

// validateProdHosts returns a deduped array of trimmed non-empty strings
// or undefined (defaulting in fbq.ts to the in-code allowlist). Anything
// other than an array, or an array that ends up empty after cleanup, is
// treated as "not configured" so the default applies.
function validateProdHosts(input: unknown): string[] | undefined {
  if (!Array.isArray(input)) return undefined;
  const seen = new Set<string>();
  const out: string[] = [];
  for (const entry of input) {
    if (typeof entry !== 'string') continue;
    const v = entry.trim().toLowerCase();
    if (!v || seen.has(v)) continue;
    seen.add(v);
    out.push(v);
  }
  return out.length > 0 ? out : undefined;
}

function validateForm(input: unknown): PixelFormConfig | undefined {
  if (!input || typeof input !== 'object') return undefined;
  const raw = input as Record<string, unknown>;
  const selector = typeof raw.selector === 'string' ? raw.selector.trim() : '';
  const formId = typeof raw.formId === 'string' ? raw.formId.trim() : '';
  if (!selector || !formId) {
    warn('form entry missing selector or formId');
    return undefined;
  }
  const pixelEventName =
    raw.pixelEventName === 'Lead' ||
    raw.pixelEventName === 'CompleteRegistration' ||
    raw.pixelEventName === 'Schedule'
      ? raw.pixelEventName
      : inferPixelEventName(formId);
  const fieldMap =
    raw.fieldMap && typeof raw.fieldMap === 'object'
      ? (raw.fieldMap as Record<string, FieldMapEntry>)
      : undefined;
  const dataLayerEventName = trimmedOrUndefined(raw.dataLayerEventName);
  const posthogEventName = trimmedOrUndefined(raw.posthogEventName);
  // Tri-state: true/false are explicit overrides, anything else stays
  // undefined so the global PixelConfig.apiPost decides at submit time.
  const apiPost = typeof raw.apiPost === 'boolean' ? raw.apiPost : undefined;
  return {
    selector,
    formId,
    pixelEventName,
    dataLayerEventName,
    posthogEventName,
    fieldMap,
    apiPost,
  };
}

// trimmedOrUndefined returns the input trimmed when it is a non-empty
// string, and undefined for anything else (non-string, empty, whitespace).
// Used so an accidental space in optional config fields does not silently
// disable an integration -- the validator surfaces "absent" cleanly rather
// than firing with " consultation " or " lead_submitted ".
function trimmedOrUndefined(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

/**
 * Default pixelEventName based on formId. Browser fbq and any server-side
 * sender (Cortana since PED-111) must use the same event name OR Meta will
 * count the conversion twice (different events do not dedup).
 */
function inferPixelEventName(formId: string): 'Lead' | 'CompleteRegistration' | 'Schedule' {
  const name = formId.trim();
  if (name === 'cal-booking') return 'Schedule';
  if (name === 'employee-benefits-broker-step-2') return 'CompleteRegistration';
  if (name.startsWith('lp-') && name.endsWith('-step-2')) return 'CompleteRegistration';
  return 'Lead';
}

function validateCal(input: unknown): PixelCalConfig | undefined {
  if (!input || typeof input !== 'object') return undefined;
  const raw = input as Record<string, unknown>;
  return {
    enabled: raw.enabled === true,
    namespace: trimmedOrUndefined(raw.namespace) ?? 'consultation',
    formId: trimmedOrUndefined(raw.formId) ?? 'cal-booking',
  };
}

function validateUtmLinkDecoration(input: unknown): PixelUtmLinkDecorationConfig | undefined {
  if (!input || typeof input !== 'object') return undefined;
  const raw = input as Record<string, unknown>;
  return {
    enabled: raw.enabled === true,
    targetHost: trimmedOrUndefined(raw.targetHost),
  };
}

function warn(msg: string): void {
  try {
    if (typeof console !== 'undefined' && console.warn) {
      console.warn('[ignition-pixel] ' + msg);
    }
  } catch {
    /* noop */
  }
}
